// src/components/dashboard/charts/FoodSectorEnergyVectorChart.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import { getEnergyColor } from "../../../config/colorScheme";
import FOOD from "../../../data/foodEnergyVectors";
import TRANSPORT from "../../../data/transportEnergyVectors";
import BULDINGS from "../../../data/buildingsEnergyVectors";
import INDUSTRY from "../../../data/industryEnergyVectors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler);

export default function FoodSectorEnergyVectorChart({
  scenario,
  from = 2025,
  to = 2050,
  title = "Energy Demand by Vector",
  includeOther = true,       // if false, still auto-show "Other" when it's all we have
}) {
  // Pull from the static table and build series
  const { labels, vectorSeries } = useMemo(() => {
    const table = FOOD?.[scenario] || {};
    const allYears = Object.keys(table).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    const years = allYears.filter((y) => y >= from && y <= to);

    const series = years.map((y) => {
      const r = table[y] || {};
      return {
        electricity: Number(r.electricity) || 0,
        hydrogen:    Number(r.hydrogen)    || 0,
        fossil:      Number(r.fossil)      || 0,
        biofuels:    Number(r.biofuels)    || 0,
        other:       Number(r.other)       || 0,
      };
    });

    return { labels: years.map(String), vectorSeries: series };
  }, [scenario, from, to]);

  const chart = useMemo(() => {
    const y = vectorSeries ?? [];

    const electricity = y.map(r => r.electricity ?? 0);
    const hydrogen    = y.map(r => r.hydrogen ?? 0);
    const fossil      = y.map(r => r.fossil ?? 0);
    const biofuels    = y.map(r => r.biofuels ?? 0);
    const other       = y.map(r => r.other ?? 0);

    // If we’ve got only "Other" energy (no electricity/hydrogen/fossil),
    // force include "Other" so the chart isn’t empty.
    const nonOtherTotal = [...electricity, ...hydrogen, ...fossil].reduce((a, v) => a + v, 0);
    const otherTotal    = other.reduce((a, v) => a + v, 0);
    const showOtherAuto = nonOtherTotal === 0 && otherTotal > 0;

    const showOther = includeOther || showOtherAuto;

    const ds = [
      {
        label: "Electricity",
        data: electricity,
        borderColor: getEnergyColor?.("electricity", "primary") ?? "#3b82f6",
        backgroundColor: getEnergyColor?.("electricity", "light") ?? "rgba(59,130,246,0.25)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
      },
      {
        label: "Hydrogen",
        data: hydrogen,
        borderColor: getEnergyColor?.("hydrogen", "primary") ?? "#06b6d4",
        backgroundColor: getEnergyColor?.("hydrogen", "light") ?? "rgba(6,182,212,0.25)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
      },
      {
        label: "Fossil fuels",
        data: fossil,
        borderColor: getEnergyColor?.("fossil", "primary") ?? "#ef4444",
        backgroundColor: getEnergyColor?.("fossil", "light") ?? "rgba(239,68,68,0.25)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
      },
      {
        label: "Biofuels",
        data: biofuels,
        borderColor: getEnergyColor?.("biofuels", "primary") ?? "#a855f7",
        backgroundColor: getEnergyColor?.("biofuels", "light") ?? "rgba(168,85,247,0.20)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
      },
    ];

    if (showOther) {
      ds.push({
        label: "Other (bio, heat, etc.)",
        data: other,
        borderColor: getEnergyColor?.("other", "primary") ?? "#a855f7",
        backgroundColor: getEnergyColor?.("other", "light") ?? "rgba(168,85,247,0.20)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
      });
    }

    return { labels, datasets: ds };
  }, [labels, vectorSeries, includeOther]);

  if (!labels?.length) {
    return <div className="w-full h-full text-xs text-gray-600">No data in range for this scenario.</div>;
  }

  return (
    <Line
      data={chart}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: title, font: { size: 14 } },
          legend: { position: "bottom" },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          x: { title: { display: true, text: "Year" } },
          y: {
            min: 0,
            title: { display: true, text: "TWh" },
            stacked: true,
            beginAtZero: true,
          },
        },
        interaction: { mode: "index", intersect: false },
      }}
    />
  );
}
